// İSG uzmanı portalı — Risk Değerlendirme oluştur editörü (api-contract §19.1). Tek sayfa:
// **meta** (başlık/saha/alan/tarih/sonraki gözden geçirme/notlar) + **inline tehlike kalemleri**
// (RHF `useFieldArray`; her kalem: tehlike + risk + etkilenenler + mevcut önlemler +
// **Olasılık×Şiddet 5x5 matris seçici** + alınacak önlem + **artık risk** matrisi). Kaydet →
// `POST /risk-assessments` (meta + inline items) → detaya yönlenir.
//
// **⛔ score/level GÖNDERİLMEZ (§19).** Kalem yalnız `probability`/`severity` (+ residual) taşır;
// matris rozeti canlı GÖSTERİM içindir, kalıcı band sunucudan gelir. **DÖF create'te YOKTUR** —
// kalem oluştuktan sonra detay ekranında (§19.4) eklenir (create gövdesi + create yanıtı kalem
// uuid'i taşımaz; DÖF kaleme bağlıdır).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  PageHeader,
  type RiskAssessmentCreateRequest,
  Select,
  type SelectOption,
  Spinner,
  resolveApiErrorMessage,
  riskApi,
  settingsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { RiskItemScoreFields } from '../../components/risk/RiskItemScoreFields';
import {
  type RiskEditorFormValues,
  emptyRiskItem,
  toRiskItemRequest,
} from '../../components/risk/riskEditorForm';
import { riskKeys } from '../../lib/riskKeys';

const OPTIONS_PAGE_SIZE = 200;

/** `yyyy-MM-dd` (yerel gün başlangıcı) → UTC ISO wire (§4); boş → null. */
function dateToIso(dateStr: string): string | null {
  if (!dateStr) {
    return null;
  }
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function RiskAssessmentEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sitesQuery = useQuery({
    queryKey: ['risk', 'options', 'sites'],
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    staleTime: 60_000,
  });

  const siteOptions: SelectOption[] = useMemo(
    () =>
      (sitesQuery.data?.items ?? []).map((s) => ({
        value: s.uuid,
        label: s.branchName ? `${s.name} · ${s.branchName}` : s.name,
      })),
    [sitesQuery.data],
  );

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().trim().min(1, t('risk.editor.validation.titleRequired')),
        siteUuid: z.string().min(1, t('risk.editor.validation.siteRequired')),
        area: z.string(),
        assessmentDate: z.string().min(1, t('risk.editor.validation.assessmentDateRequired')),
        nextReviewDate: z.string(),
        notes: z.string(),
        items: z.array(
          z
            .object({
              hazard: z.string().trim().min(1, t('risk.editor.validation.hazardRequired')),
              risk: z.string(),
              affectedPersons: z.string(),
              existingControls: z.string(),
              probability: z.number().nullable(),
              severity: z.number().nullable(),
              recommendedControls: z.string(),
              residualProbability: z.number().nullable(),
              residualSeverity: z.number().nullable(),
            })
            .superRefine((item, ctx) => {
              if (item.probability == null || item.severity == null) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['probability'],
                  message: t('risk.editor.validation.scoreRequired'),
                });
              }
              // Artık risk ya birlikte ya hiç (§19 residual INCOMPLETE).
              if ((item.residualProbability == null) !== (item.residualSeverity == null)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ['residualProbability'],
                  message: t('risk.editor.validation.residualIncomplete'),
                });
              }
            }),
        ),
      }),
    [t],
  );

  const methods = useForm<RiskEditorFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      siteUuid: '',
      area: '',
      assessmentDate: '',
      nextReviewDate: '',
      notes: '',
      items: [emptyRiskItem()],
    },
  });

  const mutation = useMutation({
    mutationFn: (values: RiskEditorFormValues) => {
      const body: RiskAssessmentCreateRequest = {
        title: values.title.trim(),
        siteUuid: values.siteUuid,
        area: values.area.trim().length > 0 ? values.area.trim() : null,
        assessmentDate: dateToIso(values.assessmentDate) ?? new Date().toISOString(),
        nextReviewDate: dateToIso(values.nextReviewDate),
        notes: values.notes.trim().length > 0 ? values.notes.trim() : null,
        items:
          values.items.length > 0
            ? values.items.map((item, index) => toRiskItemRequest(item, index + 1))
            : undefined,
      };
      return riskApi.createRiskAssessment(body);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: riskKeys.list() });
      queryClient.invalidateQueries({ queryKey: riskKeys.access() });
      navigate(`/risk-assessments/${saved.uuid}`);
    },
  });

  return (
    <FormProvider {...methods}>
      {/* `pb-20` (owner review B2 item 4) — aşağıdaki sticky alt aksiyon çubuğu için
          (`TrainingEditorPage.tsx`'in AYNI deseni). */}
      <div className="flex flex-col gap-6 pb-20">
        <Breadcrumb
          items={[
            { label: t('risk.title'), href: '/risk-assessments' },
            { label: t('risk.editor.createTitle') },
          ]}
        />
        <PageHeader title={t('risk.editor.createTitle')} />

        <form
          className="flex flex-col gap-6"
          onSubmit={methods.handleSubmit((values) => mutation.mutate(values))}
          noValidate
        >
          {/* `max-w-[960px]` (owner review B2 item 4) — okunur form sütunu genişliği. owner review
              B4-3: sticky aksiyon çubuğu artık BU sarmalayıcının İÇİNDE (son çocuk) — önceki
              sürüm çubuğu bunun DIŞINA, `<form>`'un tam genişliğine koyuyordu; 2560px'te Oluştur
              düğmesi son alandan ~1100px sağda kalıyordu. `TrainingEditorPage.tsx`'in AYNI
              deseni (çubuk `max-w-[960px]` sütununun içinde). */}
          <div className="flex max-w-[960px] flex-col gap-6">
            {mutation.isError ? (
              <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
            ) : null}

            {/* Meta */}
            <Card>
              <CardContent>
                <fieldset className="flex flex-col gap-4">
                  <legend className="text-sm font-semibold text-fg">
                    {t('risk.editor.section.meta')}
                  </legend>
                  <Field
                    htmlFor="risk-title"
                    label={t('risk.editor.field.title')}
                    error={methods.formState.errors.title?.message}
                    required
                  >
                    <Input
                      id="risk-title"
                      invalid={Boolean(methods.formState.errors.title)}
                      {...methods.register('title')}
                    />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      htmlFor="risk-site"
                      label={t('risk.editor.field.site')}
                      error={methods.formState.errors.siteUuid?.message}
                      required
                    >
                      <Select
                        id="risk-site"
                        invalid={Boolean(methods.formState.errors.siteUuid)}
                        disabled={sitesQuery.isPending}
                        placeholder={t('risk.editor.sitePlaceholder')}
                        options={siteOptions}
                        {...methods.register('siteUuid')}
                      />
                    </Field>
                    <Field htmlFor="risk-area" label={t('risk.editor.field.area')}>
                      <Input id="risk-area" {...methods.register('area')} />
                    </Field>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      htmlFor="risk-assessment-date"
                      label={t('risk.editor.field.assessmentDate')}
                      error={methods.formState.errors.assessmentDate?.message}
                      required
                    >
                      <Input
                        id="risk-assessment-date"
                        type="date"
                        invalid={Boolean(methods.formState.errors.assessmentDate)}
                        {...methods.register('assessmentDate')}
                      />
                    </Field>
                    <Field
                      htmlFor="risk-next-review"
                      label={t('risk.editor.field.nextReviewDate')}
                      hint={t('risk.editor.field.nextReviewDateHint')}
                    >
                      <Input
                        id="risk-next-review"
                        type="date"
                        {...methods.register('nextReviewDate')}
                      />
                    </Field>
                  </div>
                  <Field htmlFor="risk-notes" label={t('risk.editor.field.notes')}>
                    <Input id="risk-notes" {...methods.register('notes')} />
                  </Field>
                </fieldset>
              </CardContent>
            </Card>

            {/* Tehlike kalemleri */}
            <Card>
              <CardContent>
                <HazardItems />
              </CardContent>
            </Card>

            {/* Sticky alt aksiyon çubuğu — owner review B4-3: `max-w-[960px]` sarmalayıcının
                İÇİNDE (`TrainingEditorPage.tsx`'in AYNI deseni), form sütununun genişliğini
                aşmaz. `border-border-strong` (owner review B4-4) — hairline `border-border`
                beyaz Card'ın üstünde neredeyse görünmez kalıp çubuğu altındaki alanın bir
                PARÇASIYMIŞ gibi gösteriyordu; daha koyu kenar göz için okunur bir ayrım verir. */}
            <div className="sticky bottom-0 z-[var(--z-base)] flex items-center justify-end gap-2 border-t border-border-strong bg-surface px-4 py-3 sm:px-6">
              <Button
                type="button"
                variant="secondary"
                disabled={mutation.isPending}
                onClick={() => navigate('/risk-assessments')}
              >
                {t('risk.common.cancel')}
              </Button>
              <Button type="submit" loading={mutation.isPending}>
                {mutation.isPending ? (
                  <Spinner label={t('risk.common.create')} />
                ) : (
                  t('risk.common.create')
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
}

/** Inline tehlike kalemleri (useFieldArray) — ekle/sil; her biri HazardItemCard. */
function HazardItems() {
  const { t } = useTranslation();
  const { control } = useFormContext<RiskEditorFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  return (
    <fieldset className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <legend className="text-sm font-semibold text-fg">{t('risk.editor.section.items')}</legend>
        {/* owner review B4-8 — `secondary` (dolu gri) altındaki Oluştur/Vazgeç çiftiyle YARIŞIYOR;
            `ghost` (mevcut, EN HAFİF varyant) — `RiskItemsPanel.tsx`'in AYNI "satır ekle"
            aksiyonuyla (`onAddAction`) aynı seçim. Yeni bir varyant İCAT EDİLMEDİ. */}
        <Button type="button" size="sm" variant="ghost" onClick={() => append(emptyRiskItem())}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('risk.editor.items.add')}
        </Button>
      </div>
      <p className="-mt-2 text-xs text-muted">{t('risk.editor.items.hint')}</p>

      {fields.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-strong px-3 py-6 text-center text-sm text-muted">
          {t('risk.editor.items.empty')}
        </p>
      ) : (
        <ol className="flex flex-col gap-4">
          {fields.map((field, index) => (
            <HazardItemCard key={field.id} index={index} onRemove={() => remove(index)} />
          ))}
        </ol>
      )}
    </fieldset>
  );
}

interface HazardItemCardProps {
  index: number;
  onRemove: () => void;
}

function HazardItemCard({ index, onRemove }: HazardItemCardProps) {
  const { t } = useTranslation();
  const { register, watch, setValue, trigger, formState } = useFormContext<RiskEditorFormValues>();
  const itemErrors = formState.errors.items?.[index];

  const probability = watch(`items.${index}.probability`);
  const severity = watch(`items.${index}.severity`);
  const residualProbability = watch(`items.${index}.residualProbability`);
  const residualSeverity = watch(`items.${index}.residualSeverity`);

  return (
    <li className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
          {index + 1}
        </span>
        <div className="flex-1">
          <Field
            htmlFor={`item-${index}-hazard`}
            label={t('risk.item.field.hazard')}
            error={itemErrors?.hazard?.message}
            required
          >
            <Input
              id={`item-${index}-hazard`}
              invalid={Boolean(itemErrors?.hazard)}
              {...register(`items.${index}.hazard`)}
            />
          </Field>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-6"
          onClick={onRemove}
          aria-label={t('risk.editor.items.remove')}
        >
          <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
        </Button>
      </div>

      <div className="grid gap-4 pl-9 sm:grid-cols-2">
        <Field htmlFor={`item-${index}-risk`} label={t('risk.item.field.risk')}>
          <Input id={`item-${index}-risk`} {...register(`items.${index}.risk`)} />
        </Field>
        <Field htmlFor={`item-${index}-affected`} label={t('risk.item.field.affectedPersons')}>
          <Input id={`item-${index}-affected`} {...register(`items.${index}.affectedPersons`)} />
        </Field>
        <Field htmlFor={`item-${index}-existing`} label={t('risk.item.field.existingControls')}>
          <Input id={`item-${index}-existing`} {...register(`items.${index}.existingControls`)} />
        </Field>
        <Field
          htmlFor={`item-${index}-recommended`}
          label={t('risk.item.field.recommendedControls')}
        >
          <Input
            id={`item-${index}-recommended`}
            {...register(`items.${index}.recommendedControls`)}
          />
        </Field>
      </div>

      <div className="pl-9">
        <RiskItemScoreFields
          idPrefix={`item-${index}`}
          probability={probability}
          severity={severity}
          onScore={(p, s) => {
            // owner review B4-2 (bkz. `RiskItemFormModal.tsx`'in AYNI notu) — iki ayrı
            // `{shouldValidate:true}` çağrısı `probability`'deki `superRefine` hatasını temizlemez;
            // ikisini de sessiz yaz, SONRA tek `trigger` ile birlikte doğrula.
            setValue(`items.${index}.probability`, p, { shouldValidate: false });
            setValue(`items.${index}.severity`, s, { shouldValidate: false });
            void trigger([`items.${index}.probability`, `items.${index}.severity`]);
          }}
          residualProbability={residualProbability}
          residualSeverity={residualSeverity}
          onResidual={(p, s) => {
            setValue(`items.${index}.residualProbability`, p, { shouldValidate: false });
            setValue(`items.${index}.residualSeverity`, s, { shouldValidate: false });
            void trigger([`items.${index}.residualProbability`, `items.${index}.residualSeverity`]);
          }}
          onResidualClear={() => {
            setValue(`items.${index}.residualProbability`, null, { shouldValidate: false });
            setValue(`items.${index}.residualSeverity`, null, { shouldValidate: false });
            void trigger([`items.${index}.residualProbability`, `items.${index}.residualSeverity`]);
          }}
          scoreError={itemErrors?.probability?.message}
          residualError={itemErrors?.residualProbability?.message}
        />
      </div>
    </li>
  );
}
